import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ProgressStepper from '../components/ProgressStepper';
import InlineAlert from '../components/InlineAlert';
import { API_CONFIG, DEBUG_CONFIG } from '../config';
import { useStepTitle } from '../hooks/usePageTitle';
import { createSalesforceCustomer, isSalesforceAvailable } from '../services/salesforceService';
import { isSapAvailable } from '../services/sapService';
import { 
  formatPhoneNumber, 
  formatCreditCard, 
  formatExpiry, 
  generateCustomerId,
  getCurrentDate,
  simulateApiDelay
} from '../utils/helpers';

const NewCustomer = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreditChecking, setIsCreditChecking] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [creditResults, setCreditResults] = useState(null);
  const [showStartOverDialog, setShowStartOverDialog] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiErrorDetails, setApiErrorDetails] = useState(null);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  
  // SFDC Integration state
  const [sfdcError, setSfdcError] = useState(null);
  const [sfdcSuccess, setSfdcSuccess] = useState(false);
  const [sfdcAccountId, setSfdcAccountId] = useState(null);
  
  // SAP Integration state
  const [sapError, setSapError] = useState(null);
  const [sapSuccess, setSapSuccess] = useState(false);
  const [sapBusinessPartnerId, setSapBusinessPartnerId] = useState(null);
  
  const [lookupData, setLookupData] = useState({
    phone: '',
    email: ''
  });
  const [lookupValidationError, setLookupValidationError] = useState('');
  const [creditCheckError, setCreditCheckError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [createCustomerError, setCreateCustomerError] = useState('');

  const [customerData, setCustomerData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    ssnLast4: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    billingZip: '',
    serviceStartDate: getCurrentDate(),
    serviceType: 'residential',
    _autoPopulated: false
  });

  const steps = [
    { label: 'Customer Lookup', description: 'Search existing customers' },
    { label: 'Customer Info', description: 'Personal & service details' },
    { label: 'Confirmation', description: 'Review & verify' },
    { label: 'Complete', description: 'Account created' }
  ];

  // Update page title based on current step
  useStepTitle('New Customer Registration', currentStep, steps.length, steps[currentStep]?.label);

  const testCustomers = {
    '3867347890': {
      firstName: 'Sarah',
      lastName: 'Johnson',
      phone: '(386) 734-7890',
      email: 'sarah.johnson@email.com',
      ssnLast4: '7890', // Starts with 7 = Approved, no deposit
      streetAddress: '123 Maple Street',
      city: 'Charlotte',
      state: 'NC',
      zipCode: '28202',
      creditScore: 745,
      deposit: 0
    },
    '3051234567': {
      firstName: 'Michael',
      lastName: 'Chen',
      phone: '(305) 123-4567',
      email: 'michael.chen@email.com',
      ssnLast4: '4567', // Starts with 4 = Requires deposit
      streetAddress: '456 Oak Avenue',
      city: 'Miami',
      state: 'FL',
      zipCode: '33101',
      creditScore: 580,
      deposit: 250
    }
  };

  useEffect(() => {
    setCustomerData(prev => ({ ...prev, serviceStartDate: getCurrentDate() }));
  }, []);

  const handleLookupChange = (e) => {
    const { name, value } = e.target;
    setLookupValidationError('');
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setLookupData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setLookupData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    if (creditCheckError) setCreditCheckError('');
    if (paymentError) setPaymentError('');
    
    let formattedValue = value;
    
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (name === 'cardNumber') {
      formattedValue = formatCreditCard(value);
    } else if (name === 'expiry') {
      formattedValue = formatExpiry(value);
    } else if (name === 'ssnLast4' || name === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
    }
    
    setCustomerData(prev => ({
      ...prev,
      [name]: formattedValue,
      // Clear auto-populated flag when user manually edits phone or email
      _autoPopulated: (name === 'phone' || name === 'email') ? false : prev._autoPopulated
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!lookupData.phone && !lookupData.email) {
      setLookupValidationError('Please enter either a phone number or email address.');
      return;
    }

    setLookupValidationError('');

    setIsSearching(true);
    setShowResults(false);
    
    try {
      await simulateApiDelay(1500);
      
      // Simulate search logic
      const results = [];
      if (lookupData.phone) {
        const cleanPhone = lookupData.phone.replace(/\D/g, '');
        if (testCustomers[cleanPhone]) {
          results.push({
            ...testCustomers[cleanPhone],
            matchType: 'Phone Number',
            confidence: 'High'
          });
        }
      }
      
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTestCustomer = (phoneNumber) => {
    const customer = testCustomers[phoneNumber];
    if (customer) {
      setCustomerData(prev => ({
        ...prev,
        ...customer
      }));
      setCurrentStep(1);
    }
  };

  const handleSkipLookup = () => {
    // Auto-populate phone and email from lookup data if available
    const hasLookupData = lookupData.phone || lookupData.email;
    setCustomerData(prev => ({
      ...prev,
      phone: lookupData.phone || prev.phone,
      email: lookupData.email || prev.email,
      _autoPopulated: hasLookupData // Flag to show user that data was transferred
    }));
    setCurrentStep(1);
  };

  const handleUseFakeTestData = async () => {
    setIsApiLoading(true);
    setApiError(null);
    setApiErrorDetails(null);
    
    try {
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.log('Fetching API test customer data from:', API_CONFIG.FAKE_DATA_URL);
      }
      const response = await fetch(API_CONFIG.FAKE_DATA_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const apiData = await response.json();
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.log('API Response:', apiData);
      }
      
      // Transform API data to our format (same as HTML version)
      const customer = {
        firstName: apiData.FirstName || 'Ted',
        lastName: apiData.LastName || 'McTesteron',
        phone: formatPhoneNumber(apiData.Phone_number || '5551234567'),
        email: apiData.Email_d || 'ted.mctesteron@toscademo.com',
        streetAddress: apiData.StreetAddress || '123 Main St',
        city: apiData.City || 'Atlanta',
        state: 'GA', // Default to GA as requested
        zipCode: (apiData.ZipCode || '28202').split('-')[0], // Trim last 4 digits
        serviceStartDate: getCurrentDate(),
        serviceType: 'residential'
      };
      
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.log('Transformed customer data:', customer);
      }
      
      // Fill lookup form with API phone number
      setLookupData({
        phone: customer.phone,
        email: customer.email
      });
      
      // Simulate search results showing no existing customer
      setTimeout(() => {
        setSearchResults([]);
        setShowResults(true);
        
        // After showing search results, auto-populate the customer form
        setTimeout(() => {
          setCustomerData(prev => ({
            ...prev,
            ...customer
          }));
          
          // Move to customer info step
          setCurrentStep(1);
          
          if (DEBUG_CONFIG.LOG_API_CALLS) {
            console.log('API customer data populated successfully');
          }
          
        }, 500);
      }, 1000);
      
    } catch (error) {
      console.error('API Error:', error);
      
      // Set error state for display
      setApiError(error.message);
      setApiErrorDetails({
        url: API_CONFIG.FAKE_DATA_URL,
        error: error.toString(),
        time: new Date().toLocaleString()
      });
      
    } finally {
      setIsApiLoading(false);
    }
  };

  const handleCreditCheck = async () => {
    setHasAttemptedCreditCheck(true);
    
    if (!areRequiredFieldsValid()) {
      setCreditCheckError('Please complete all required fields before running credit check.');
      return;
    }

    setCreditCheckError('');

    setIsCreditChecking(true);
    
    try {
      await simulateApiDelay(2000);
      
      // Get first digit of SSN last 4 digits
      const firstDigit = parseInt(customerData.ssnLast4.charAt(0));
      
      let creditScore, deposit, status;
      
      if (firstDigit >= 7) {
        // SSN starts with 7 or higher = Approved, no deposit
        creditScore = Math.floor(Math.random() * 201) + 650; // 650-850
        deposit = 0;
        status = 'Approved';
      } else {
        // SSN starts with 6 or lower = Requires deposit
        creditScore = Math.floor(Math.random() * 200) + 450; // 450-649
        deposit = 250;
        status = 'Conditional Approval - Deposit Required';
      }
      
      setCreditResults({
        creditScore,
        deposit,
        status,
        requiresPayment: deposit > 0
      });
    } catch (error) {
      console.error('Credit check error:', error);
    } finally {
      setIsCreditChecking(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setCurrentStep(2);
  };

  const handleCreateCustomer = async () => {
    setCreateCustomerError('');
    setIsCreatingCustomer(true);
    setSfdcError(null);
    setSfdcSuccess(false);
    setSfdcAccountId(null);
    setSapError(null);
    setSapSuccess(false);
    setSapBusinessPartnerId(null);
    
    try {
      // Step 1: Create local customer record and generate TES account number
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.log('Creating local customer record...');
      }
      
      await simulateApiDelay(1000); // Simulate local customer creation
      const customerId = generateCustomerId();
      
      // Update customer data with generated ID
      const updatedCustomerData = { ...customerData, customerId, creditResults };
      setCustomerData(prev => ({ ...prev, customerId }));
      
      if (DEBUG_CONFIG.LOG_API_CALLS) {
        console.log('Local customer created with ID:', customerId);
      }

      // Step 2: Create SFDC composite record if available
      if (isSalesforceAvailable()) {
        if (DEBUG_CONFIG.LOG_API_CALLS) {
          console.log('Creating SFDC composite record...');
        }
        
        try {
          const sfdcResult = await createSalesforceCustomer(updatedCustomerData, customerId);
          setSfdcSuccess(true);
          setSfdcAccountId(sfdcResult.accountId);
          
          if (DEBUG_CONFIG.LOG_API_CALLS) {
            console.log('SFDC customer created successfully:', sfdcResult);
          }
        } catch (sfdcError) {
          console.error('SFDC creation failed, but local customer exists:', sfdcError);
          setSfdcError(sfdcError.message);
          
          // Don't fail the entire process if SFDC fails
          // Local customer record still exists
        }
      } else {
        console.warn('Salesforce integration not available - skipping SFDC creation');
      }

      // Step 2.5: Create SAP Business Partner if available and SFDC succeeded
      if (isSapAvailable()) {
        if (DEBUG_CONFIG.LOG_API_CALLS) {
          console.log('Creating SAP Business Partner...');
        }

        // Temporarily disable SAP API calls (SAP demo endpoint returns 401 + WWW-Authenticate,
        // which triggers the browser's login prompt when proxied through Vite).
        setSapSuccess(false);
        setSapBusinessPartnerId(null);
        setSapError('SAP integration is temporarily disabled in this build. Customer was created locally and Salesforce sync can continue.');
      } else {
        console.warn('SAP integration not available - skipping SAP Business Partner creation');
      }

      // Step 3: Move to success step regardless of integration status
      await simulateApiDelay(500); // Final processing delay
      setCurrentStep(3);
      
    } catch (error) {
      console.error('Customer creation error:', error);
      // Handle local customer creation failure
      setCreateCustomerError('Failed to create customer account. Please try again.');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  // Handle step navigation (going back to completed steps)
  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  // Check if user is in progress (past first step) - used for start over dialog
  const isInProgress = useCallback(() => {
    return currentStep > 0 || 
           lookupData.phone !== '' || 
           lookupData.email !== '' ||
           customerData.firstName !== '' ||
           customerData.lastName !== '' ||
           searchResults.length > 0 ||
           creditResults !== null ||
           paymentSuccess ||
           apiError !== null;
  }, [currentStep, lookupData, customerData, searchResults, creditResults, paymentSuccess, apiError]);

  // Function to trigger start over dialog (can be called from sidebar)
  const triggerStartOverCheck = useCallback(() => {
    if (isInProgress()) {
      setShowStartOverDialog(true);
    } else {
      // If not in progress, reset to first step
      setCurrentStep(0);
    }
  }, [isInProgress]);

  // Expose function globally for sidebar access
  useEffect(() => {
    window.triggerNewCustomerStartOver = triggerStartOverCheck;
    return () => {
      delete window.triggerNewCustomerStartOver;
    };
  }, [triggerStartOverCheck]);

  // Handle start over confirmation
  const handleStartOver = () => {
    // Reset all state
    setCurrentStep(0);
    setIsSearching(false);
    setIsCreditChecking(false);
    setIsApiLoading(false);
    setApiError(null);
    setApiErrorDetails(null);
    setLookupValidationError('');
    setCreditCheckError('');
    setPaymentError('');
    setCreateCustomerError('');
    setIsPaymentProcessing(false);
    setPaymentSuccess(false);
    setIsCreatingCustomer(false);
    setSfdcError(null);
    setSfdcSuccess(false);
    setSfdcAccountId(null);
    setSapError(null);
    setSapSuccess(false);
    setSapBusinessPartnerId(null);
    setSearchResults([]);
    setShowResults(false);
    setCreditResults(null);
    setHasAttemptedCreditCheck(false);
    setLookupData({ phone: '', email: '' });
    setCustomerData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      ssnLast4: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      cardNumber: '',
      expiry: '',
      cvv: '',
      billingZip: '',
      serviceStartDate: getCurrentDate(),
      serviceType: 'residential',
      _autoPopulated: false
    });
    
    // Close dialog
    setShowStartOverDialog(false);
  };

  // Handle cancel start over
  const handleCancelStartOver = () => {
    setShowStartOverDialog(false);
  };

  // Initialize component
  useEffect(() => {
    setCustomerData(prev => ({ ...prev, serviceStartDate: getCurrentDate() }));
  }, []);

  const isSSNValid = customerData.ssnLast4.length === 4;

  // Track if user has attempted to run credit check (for field validation styling)
  const [hasAttemptedCreditCheck, setHasAttemptedCreditCheck] = useState(false);

  // Check if all required fields for credit check are populated
  const areRequiredFieldsValid = () => {
    return (
      customerData.phone.trim() !== '' &&
      customerData.email.trim() !== '' &&
      customerData.firstName.trim() !== '' &&
      customerData.lastName.trim() !== '' &&
      customerData.streetAddress.trim() !== '' &&
      customerData.city.trim() !== '' &&
      customerData.state.trim() !== '' &&
      customerData.zipCode.trim() !== '' &&
      isSSNValid
    );
  };

  // Get missing required fields for better user feedback
  const getMissingFields = () => {
    const missing = [];
    if (!customerData.phone.trim()) missing.push('Phone Number');
    if (!customerData.email.trim()) missing.push('Email Address');
    if (!customerData.firstName.trim()) missing.push('First Name');
    if (!customerData.lastName.trim()) missing.push('Last Name');
    if (!customerData.streetAddress.trim()) missing.push('Street Address');
    if (!customerData.city.trim()) missing.push('City');
    if (!customerData.state.trim()) missing.push('State');
    if (!customerData.zipCode.trim()) missing.push('ZIP Code');
    if (!isSSNValid) missing.push('SSN (4 digits)');
    return missing;
  };

  // Get field validation class for styling
  const getFieldClass = (fieldName, value) => {
    const baseClass = 'form-control';
    if (hasAttemptedCreditCheck && !value.trim()) {
      return `${baseClass} is-invalid`;
    }
    return baseClass;
  };

  const getSelectClass = (fieldName, value) => {
    const baseClass = 'form-select';
    if (hasAttemptedCreditCheck && !value.trim()) {
      return `${baseClass} is-invalid`;
    }
    return baseClass;
  };

  // Check if payment fields are valid (basic validation)
  const arePaymentFieldsValid = () => {
    return (
      customerData.cardNumber.trim() !== '' &&
      customerData.expiry.trim() !== '' &&
      customerData.cvv.trim() !== '' &&
      customerData.billingZip.trim() !== '' &&
      customerData.cardNumber.replace(/\s/g, '').length >= 15 && // At least 15 digits for Amex, 16 for others
      customerData.expiry.length === 5 && // MM/YY format
      customerData.cvv.length >= 3 && // At least 3 digits
      customerData.billingZip.length === 5 // 5 digit ZIP
    );
  };

  // Handle payment processing
  const handlePayment = async () => {
    if (!arePaymentFieldsValid()) {
      setPaymentError('Please complete all payment fields with valid information.');
      return;
    }

    setPaymentError('');

    setIsPaymentProcessing(true);
    
    try {
      // Simulate payment processing delay
      await simulateApiDelay(2500);
      
      // For now, all payments succeed
      setPaymentSuccess(true);
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Payment processing failed. Please try again.');
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
{/*       <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            New Customer
          </li>
        </ol>
      </nav> */}

      <PageHeader
        eyebrow="Customer Onboarding"
        title="New Customer Registration"
        description="Create a new utility customer account, verify eligibility, and complete onboarding in a guided four-step workflow."
        icon="person-plus"
        iconTone="primary"
        className="mb-4"
        actions={(
          <span className="badge text-bg-light border">Step {currentStep + 1} of {steps.length}</span>
        )}
      />

      {/* Enhanced Progress Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-tse-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="h5 mb-0 text-white">
                  <i className="bi bi-diagram-3 me-2"></i>Registration Progress
                </h2>
                <small className="text-white-50">{steps[currentStep]?.label}</small>
              </div>
            </div>
            <div className="card-body p-0">
              <ProgressStepper 
                currentStep={currentStep} 
                steps={steps} 
                onStepClick={handleStepClick}
                showCurrentAsCompleted={currentStep === 3}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Customer Lookup */}
      {currentStep === 0 && (
        <div className="row mb-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light border-0">
                <h2 className="card-title mb-1 h5">Customer Lookup</h2>
                <p className="card-subtitle mb-0 text-muted small">Search for existing customer records</p>
              </div>
              <div className="card-body">
                <InlineAlert
                  variant="info"
                  icon="info-circle"
                  title="Before Creating New Customer"
                  message="Search our system to prevent duplicate accounts. Enter either phone number OR email address to begin."
                  className="border-0 bg-light mb-4"
                  ariaLabel="Customer lookup instructions alert"
                />
                
                <form onSubmit={handleSearch}>
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="lookupPhone" className="form-label fw-medium">Phone Number</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-telephone"></i></span>
                        <input 
                          type="tel" 
                          className={`form-control ${lookupValidationError && !lookupData.phone ? 'is-invalid' : ''}`} 
                          name="phone"
                          value={lookupData.phone}
                          onChange={handleLookupChange}
                          placeholder="(555) 123-4567"
                          aria-label="LookupPhoneInput"
                          aria-invalid={lookupValidationError && !lookupData.phone ? 'true' : 'false'}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="lookupEmail" className="form-label fw-medium">Email Address</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                        <input 
                          type="email" 
                          className={`form-control ${lookupValidationError && !lookupData.email ? 'is-invalid' : ''}`} 
                          name="email"
                          value={lookupData.email}
                          onChange={handleLookupChange}
                          placeholder="customer@example.com"
                          aria-label="LookupEmailInput"
                          aria-invalid={lookupValidationError && !lookupData.email ? 'true' : 'false'}
                        />
                      </div>
                    </div>
                  </div>

                  {lookupValidationError && (
                    <InlineAlert
                      variant="danger"
                      icon="exclamation-circle"
                      message={lookupValidationError}
                      className="py-2 mb-3"
                      ariaLabel="Customer lookup validation error"
                      dataTestId="lookup-validation-error"
                    />
                  )}
                  
                  <div className="d-flex gap-2 mb-4">
                    <button 
                      type="submit" 
                      className="btn btn-tse-primary"
                      disabled={isSearching}
                      aria-label="SearchCustomerButton"
                    >
                      {isSearching ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Searching...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-2"></i>Search Customer Database
                        </>
                      )}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={handleSkipLookup} aria-label="SkipLookupButton">
                      <i className="bi bi-arrow-right me-2"></i>Skip & Create New
                    </button>
                  </div>
                </form>

                {/* API Error Display */}
                {apiError && (
                  <InlineAlert
                    variant="danger"
                    icon="exclamation-triangle"
                    title="API Error:"
                    message={apiError}
                    className="mt-4"
                    ariaLabel="Fake test data API error alert"
                    onDismiss={() => setApiError(null)}
                  >
                    {apiErrorDetails && (
                      <div className="mt-2" aria-label="API error details">
                        <hr className="my-2" />
                        <small className="text-muted">
                          <strong>Details:</strong><br />
                          <strong>URL:</strong> {apiErrorDetails.url}<br />
                          <strong>Error:</strong> {apiErrorDetails.error}<br />
                          <strong>Time:</strong> {apiErrorDetails.time}
                        </small>
                      </div>
                    )}
                  </InlineAlert>
                )}

                {/* Search Results */}
                {showResults && (
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="mb-3">Search Results</h6>
                    {searchResults.length > 0 ? (
                      <div className="list-group">
                        {searchResults.map((result, index) => (
                          <div key={index} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h6 className="mb-1">{result.firstName} {result.lastName}</h6>
                                <p className="mb-1 text-muted">{result.phone} • {result.email}</p>
                                <small className="text-muted">Match: {result.matchType} ({result.confidence} confidence)</small>
                              </div>
                              <button className="btn btn-outline-warning btn-sm">
                                View Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <i className="bi bi-search display-4 text-muted mb-3"></i>
                        <h6 className="text-muted">No matching customers found</h6>
                        <p className="text-muted mb-3">You can proceed to create a new customer account.</p>
                        <button className="btn btn-tse-primary" onClick={handleSkipLookup}>
                          <i className="bi bi-person-plus me-2"></i>Create New Customer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-light border-0">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-lightbulb me-2 text-warning"></i>Search Guidelines
                </h6>
              </div>
              <div className="card-body">
                <div className="small">
                  <div className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                    <span>Enter either phone number OR email address</span>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                    <span>Phone format: (555) 123-4567</span>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                    <span>Use primary contact information</span>
                  </div>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                    <span>If no match found, proceed to create new account</span>
                  </div>
                </div>
              </div>
            </div>
            
            {DEBUG_CONFIG.SHOW_TEST_BUTTONS && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light border-0">
                  <h6 className="mb-0 fw-bold">
                    <i className="bi bi-tools me-2 text-tse-primary"></i>Development Tools
                  </h6>
                </div>
                <div className="card-body">
                  <p className="small text-muted mb-3">For testing purposes, use these pre-configured customer profiles or real API data:</p>
                  <div className="d-grid gap-2">
                    <button 
                      type="button" 
                      className="btn btn-outline-success btn-sm"
                      onClick={() => handleTestCustomer('3867347890')}
                    >
                      <i className="bi bi-person-check me-1"></i>
                      Approved Customer
                      <small className="d-block text-muted">SSN: 7890 (No deposit)</small>
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleTestCustomer('3051234567')}
                    >
                      <i className="bi bi-person-exclamation me-1"></i>
                      Deposit Required Customer
                      <small className="d-block text-muted">SSN: 4567 (Needs payment)</small>
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-outline-info btn-sm"
                      onClick={handleUseFakeTestData}
                      disabled={isApiLoading}
                    >
                      {isApiLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-cloud-download me-1"></i>
                          FakeTestData API
                        </>
                      )}
                      <small className="d-block text-muted">Real external API data</small>
                    </button>
                  </div>
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Test buttons auto-populate forms. API button fetches real data from FakeTestData.com
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Customer Form */}
      {currentStep === 1 && (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light border-0">
                <h2 className="card-title mb-1 h5">Customer Information</h2>
                <p className="card-subtitle mb-0 text-muted small">Enter customer details and verify credit</p>
              </div>
              <div className="card-body">
                {customerData._autoPopulated && (
                  <InlineAlert
                    variant="success"
                    icon="check-circle"
                    title="Information Auto-Populated"
                    message="Contact information from your search has been transferred to this form."
                    className="border-0 bg-light mb-4"
                    ariaLabel="Auto-populated customer information alert"
                    onDismiss={() => setCustomerData(prev => ({ ...prev, _autoPopulated: false }))}
                  />
                )}
                <form onSubmit={handleFormSubmit}>
                  {/* Personal Information */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <h3 className="h6 fw-bold text-tse-primary mb-3">Personal Information</h3>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-phone" className="form-label">
                        Phone Number <span className="text-danger">*</span>
                        {customerData._autoPopulated && customerData.phone && (
                          <small className="text-success ms-2">
                            <i className="bi bi-check-circle me-1"></i>Auto-filled
                          </small>
                        )}
                      </label>
                      <input 
                        id="customer-phone"
                        type="tel" 
                        className={getFieldClass('phone', customerData.phone)}
                        name="phone"
                        value={customerData.phone}
                        onChange={handleCustomerChange}
                        placeholder="(555) 123-4567"
                        aria-label="CustomerPhoneInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                        {customerData._autoPopulated && customerData.email && (
                          <small className="text-success ms-2">
                            <i className="bi bi-check-circle me-1"></i>Auto-filled
                          </small>
                        )}
                      </label>
                      <input 
                        id="customer-email"
                        type="email" 
                        className={getFieldClass('email', customerData.email)}
                        name="email"
                        value={customerData.email}
                        onChange={handleCustomerChange}
                        placeholder="customer@example.com"
                        aria-label="CustomerEmailInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-first-name" className="form-label">First Name <span className="text-danger">*</span></label>
                      <input 
                        id="customer-first-name"
                        type="text" 
                        className={getFieldClass('firstName', customerData.firstName)}
                        name="firstName"
                        value={customerData.firstName}
                        onChange={handleCustomerChange}
                        aria-label="CustomerFirstNameInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-last-name" className="form-label">Last Name <span className="text-danger">*</span></label>
                      <input 
                        id="customer-last-name"
                        type="text" 
                        className={getFieldClass('lastName', customerData.lastName)}
                        name="lastName"
                        value={customerData.lastName}
                        onChange={handleCustomerChange}
                        aria-label="CustomerLastNameInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-ssn-last4" className="form-label">Last 4 of SSN <span className="text-danger">*</span></label>
                      <input 
                        id="customer-ssn-last4"
                        type="text" 
                        className={getFieldClass('ssnLast4', customerData.ssnLast4)}
                        name="ssnLast4"
                        value={customerData.ssnLast4}
                        onChange={handleCustomerChange}
                        placeholder="1234"
                        maxLength="4"
                        aria-label="CustomerSSNLast4Input"
                        required 
                      />
                    </div>
                  </div>

                  {/* Service Address (Premise) */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <h3 className="h6 fw-bold text-tse-primary mb-3">Service Address (Premise)</h3>
                    </div>
                    <div className="col-12 mb-3">
                      <label htmlFor="customer-street-address" className="form-label">Street Address <span className="text-danger">*</span></label>
                      <input 
                        id="customer-street-address"
                        type="text" 
                        className={getFieldClass('streetAddress', customerData.streetAddress)}
                        name="streetAddress"
                        value={customerData.streetAddress}
                        onChange={handleCustomerChange}
                        placeholder="123 Main Street"
                        aria-label="CustomerStreetAddressInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="customer-city" className="form-label">City <span className="text-danger">*</span></label>
                      <input 
                        id="customer-city"
                        type="text" 
                        className="form-control" 
                        name="city"
                        value={customerData.city}
                        onChange={handleCustomerChange}
                        placeholder="Charlotte"
                        aria-label="CustomerCityInput"
                        required 
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label htmlFor="customer-state" className="form-label">State <span className="text-danger">*</span></label>
                      <select 
                        id="customer-state"
                        className={getSelectClass('state', customerData.state)}
                        name="state"
                        value={customerData.state}
                        onChange={handleCustomerChange}
                        aria-label="CustomerStateSelect"
                        required
                      >
                        <option value="">Select State</option>
                        <option value="NC">North Carolina</option>
                        <option value="SC">South Carolina</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="VA">Virginia</option>
                        <option value="TN">Tennessee</option>
                      </select>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label htmlFor="customer-zip-code" className="form-label">ZIP Code <span className="text-danger">*</span></label>
                      <input 
                        id="customer-zip-code"
                        type="text" 
                        className="form-control" 
                        name="zipCode"
                        value={customerData.zipCode}
                        onChange={handleCustomerChange}
                        placeholder="28202"
                        maxLength="5"
                        aria-label="CustomerZipCodeInput"
                        required 
                      />
                    </div>
                  </div>

                  {/* Credit Check Section */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <h3 className="h6 fw-bold text-tse-primary mb-3">Credit Verification</h3>
                      <p className="text-muted mb-3">We need to perform a credit check to determine deposit requirements.</p>
                      <button 
                        type="button" 
                        className="btn btn-tse-primary"
                        onClick={handleCreditCheck}
                        disabled={!areRequiredFieldsValid() || isCreditChecking}
                        aria-label="RunCreditCheckButton"
                      >
                        {isCreditChecking ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Running Credit Check...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-shield-check me-2"></i>Run Credit Check
                          </>
                        )}
                      </button>
                      {!areRequiredFieldsValid() && (
                        <div className="mt-2">
                          <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Missing required fields: {getMissingFields().join(', ')}
                          </small>
                        </div>
                      )}
                      {creditCheckError && (
                        <InlineAlert
                          variant="danger"
                          icon="exclamation-circle"
                          message={creditCheckError}
                          className="py-2 mt-3 mb-0"
                          ariaLabel="Credit check validation error"
                          dataTestId="credit-check-validation-error"
                        />
                      )}
                    </div>
                  </div>

                  {/* Credit Results */}
                  {creditResults && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-body">
                            <h6 className="card-title">Credit Check Results</h6>
                            <div className="row">
                              <div className="col-md-4">
                                <strong>Credit Score:</strong><br />
                                <span className={`h5 ${creditResults.deposit === 0 ? 'text-success' : 'text-warning'}`} aria-label="CreditScore">
                                  {creditResults.creditScore}
                                </span>
                              </div>
                              <div className="col-md-4">
                                <strong>Status:</strong><br />
                                <span className={`badge ${creditResults.deposit === 0 ? 'bg-success' : 'bg-warning'}`} aria-label="CreditStatus">
                                  {creditResults.status}
                                </span>
                              </div>
                              <div className="col-md-4">
                                <strong>Security Deposit:</strong><br />
                                <span className="h5 text-primary" aria-label="SecurityDeposit">${creditResults.deposit}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Form - Only show if deposit required */}
                  {creditResults && creditResults.requiresPayment && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="card border-warning">
                          <div className="card-header bg-warning text-dark">
                            <h6 className="mb-0 fw-bold">
                              <i className="bi bi-credit-card me-2"></i>Payment Information Required
                            </h6>
                          </div>
                          <div className="card-body">
                            {paymentSuccess ? (
                              <InlineAlert
                                variant="success"
                                icon="check-circle"
                                title="Payment Successful!"
                                message="Your security deposit payment has been processed successfully. You can now proceed to review your information."
                                className="border-0 bg-light mb-3"
                                ariaLabel="Payment successful alert"
                              />
                            ) : (
                              <p className="text-muted mb-3">A security deposit of <strong>${creditResults.deposit}</strong> is required. Please provide payment information.</p>
                            )}

                            {paymentError && (
                              <InlineAlert
                                variant="danger"
                                icon="exclamation-circle"
                                message={paymentError}
                                className="py-2 mb-3"
                                ariaLabel="Payment validation error"
                                dataTestId="payment-validation-error"
                              />
                            )}
                            
                            <div className="row">
                              <div className="col-md-8 mb-3">
                                <label htmlFor="payment-card-number" className="form-label">Card Number <span className="text-danger">*</span></label>
                                <input 
                                  id="payment-card-number"
                                  type="text" 
                                  className="form-control" 
                                  name="cardNumber"
                                  value={customerData.cardNumber}
                                  onChange={handleCustomerChange}
                                  placeholder="1234 5678 9012 3456"
                                  maxLength="19"
                                  disabled={paymentSuccess}
                                  aria-label="PaymentCardNumberInput"
                                  required 
                                />
                              </div>
                              <div className="col-md-4 mb-3">
                                <label htmlFor="payment-cvv" className="form-label">CVV <span className="text-danger">*</span></label>
                                <input 
                                  id="payment-cvv"
                                  type="text" 
                                  className="form-control" 
                                  name="cvv"
                                  value={customerData.cvv}
                                  onChange={handleCustomerChange}
                                  placeholder="123"
                                  maxLength="4"
                                  disabled={paymentSuccess}
                                  required 
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label htmlFor="payment-expiry" className="form-label">Expiry Date <span className="text-danger">*</span></label>
                                <input 
                                  id="payment-expiry"
                                  type="text" 
                                  className="form-control" 
                                  name="expiry"
                                  value={customerData.expiry}
                                  onChange={handleCustomerChange}
                                  placeholder="MM/YY"
                                  maxLength="5"
                                  disabled={paymentSuccess}
                                  required 
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label htmlFor="payment-billing-zip" className="form-label">Billing ZIP Code <span className="text-danger">*</span></label>
                                <input 
                                  id="payment-billing-zip"
                                  type="text" 
                                  className="form-control" 
                                  name="billingZip"
                                  value={customerData.billingZip}
                                  onChange={handleCustomerChange}
                                  placeholder="12345"
                                  maxLength="5"
                                  disabled={paymentSuccess}
                                  required 
                                />
                              </div>
                            </div>

                            {!paymentSuccess && (
                              <div className="d-flex gap-2 mb-3">
                                <button 
                                  type="button" 
                                  className="btn btn-success"
                                  onClick={handlePayment}
                                  disabled={!arePaymentFieldsValid() || isPaymentProcessing}
                                >
                                  {isPaymentProcessing ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Processing Payment...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-credit-card me-2"></i>Process Payment (${creditResults.deposit})
                                    </>
                                  )}
                                </button>
                                {!arePaymentFieldsValid() && (
                                  <div className="align-self-center">
                                    <small className="text-muted">
                                      <i className="bi bi-info-circle me-1"></i>
                                      Please complete all payment fields
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <InlineAlert
                              variant="info"
                              icon="info-circle"
                              title="Secure Payment Processing"
                              message="Your payment information is encrypted and processed securely. The deposit will be charged when service begins."
                              className="border-0 bg-light mt-3"
                              ariaLabel="Secure payment processing information alert"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Service Information */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <h3 className="h6 fw-bold text-tse-primary mb-3">Service Information</h3>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="service-start-date" className="form-label">Service Start Date <span className="text-danger">*</span></label>
                      <input 
                        id="service-start-date"
                        type="date" 
                        className="form-control" 
                        name="serviceStartDate"
                        value={customerData.serviceStartDate}
                        onChange={handleCustomerChange}
                        aria-label="ServiceStartDateInput"
                        required 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="service-type" className="form-label">Service Type</label>
                      <select 
                        id="service-type"
                        className="form-select" 
                        name="serviceType"
                        value={customerData.serviceType}
                        onChange={handleCustomerChange}
                        aria-label="ServiceTypeSelect"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>                        
                      </select>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <button 
                      type="submit" 
                      className="btn btn-tse-primary"
                      disabled={!creditResults || (creditResults.requiresPayment && !paymentSuccess)}
                      aria-label="ReviewInformationButton"
                    >
                      <i className="bi bi-check-circle me-2"></i>Review Information
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary"
                      onClick={() => setCurrentStep(0)}
                    >
                      <i className="bi bi-arrow-left me-2"></i>Back to Lookup
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {currentStep === 2 && (
        <div className="row">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light border-0">
                <h2 className="card-title mb-1 h5">Confirm Customer Information</h2>
                <p className="card-subtitle mb-0 text-muted small">Review details before account creation</p>
              </div>
              <div className="card-body">
                <p className="text-muted mb-4">Please review the information below before creating the customer account.</p>

                {createCustomerError && (
                  <InlineAlert
                    variant="danger"
                    icon="exclamation-circle"
                    message={createCustomerError}
                    ariaLabel="Create customer error alert"
                    dataTestId="create-customer-error"
                  />
                )}
                
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold">Personal Information</h6>
                    <p><strong>Name:</strong> {customerData.firstName} {customerData.lastName}</p>
                    <p><strong>Phone:</strong> {customerData.phone}</p>
                    <p><strong>Email:</strong> {customerData.email}</p>
                    <p><strong>Address:</strong> {customerData.streetAddress}, {customerData.city}, {customerData.state} {customerData.zipCode}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold">Service Information</h6>
                    <p><strong>Start Date:</strong> {customerData.serviceStartDate}</p>
                    <p><strong>Service Type:</strong> {customerData.serviceType}</p>
                    {creditResults && (
                      <>
                        <p><strong>Credit Status:</strong> {creditResults.status}</p>
                        <p>
                          <strong>Security Deposit:</strong> ${creditResults.deposit}
                          {creditResults.requiresPayment && paymentSuccess && (
                            <span className="text-success ms-2">
                              <i className="bi bi-check-circle-fill me-1"></i>
                              Payment Accepted
                            </span>
                          )}
                        </p>
                        {creditResults.requiresPayment && (
                          <p><strong>Payment Method:</strong> Card ending in {customerData.cardNumber.slice(-4)}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2 mt-4">
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={handleCreateCustomer}
                    disabled={isCreatingCustomer}
                    aria-label="CreateCustomerButton"
                  >
                    {isCreatingCustomer ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating Customer Account...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-plus me-2"></i>Create Customer & Schedule Move-In
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary"
                    onClick={() => setCurrentStep(1)}
                    disabled={isCreatingCustomer}
                  >
                    <i className="bi bi-pencil me-2"></i>Edit Information
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {currentStep === 3 && (
        <div className="row">
          <div className="col-lg-8">
            <div className="card border-success">
              <div className="card-header bg-success text-white">
                <h2 className="card-title mb-0 h5">
                  <i className="bi bi-check-circle me-2"></i>
                  Customer Created Successfully!
                </h2>
              </div>
              <div className="card-body">
                <InlineAlert
                  variant="success"
                  icon="check-circle"
                  title="Welcome to TriServe Energy!"
                  message="The new customer account has been created successfully."
                  ariaLabel="Customer created successfully alert"
                >
                  <hr />
                  <div className="row">
                    <div className="col-sm-6">
                      <strong>Customer Account Number:</strong><br />
                        <span className="h4 text-success" aria-label="Customer Account Number">{customerData.customerId}</span>
                    </div>
                    <div className="col-sm-6">
                      <strong>Service Start Date:</strong><br />
                        <span aria-label="Service Start Date">{customerData.serviceStartDate}</span>
                    </div>
                  </div>
                </InlineAlert>

                {/* SFDC Integration Status */}
                {isSalesforceAvailable() && (
                  <div className="mb-3" aria-label="SalesforceIntegrationStatus">
                    {sfdcSuccess ? (
                      <InlineAlert
                        variant="info"
                        icon="cloud-check"
                        title="Salesforce Integration Successful"
                        message="Customer record has been synced to Salesforce CRM with Account, Contact, and Opportunity records."
                        className="border-0 bg-light"
                        ariaLabel="Salesforce integration successful alert"
                      >
                        {sfdcAccountId && (
                          <p className="mb-1 small">
                            <strong>SFDC Account ID:</strong> <span className="font-monospace text-primary" aria-label="SFDC Account ID">{sfdcAccountId}</span>
                          </p>
                        )}
                        {sapBusinessPartnerId && (
                          <p className="mb-0 small">
                            <strong>SAP Business Partner ID:</strong> <span className="font-monospace text-primary" aria-label="SapBusinessPartnerId">{sapBusinessPartnerId}</span>
                          </p>
                        )}
                      </InlineAlert>
                    ) : sfdcError ? (
                      <InlineAlert
                        variant="warning"
                        icon="exclamation-triangle"
                        title="Salesforce Sync Warning"
                        message="Customer created locally but Salesforce sync failed. The customer service team has been notified."
                        className="border-0 bg-light"
                        ariaLabel="Salesforce sync warning alert"
                      >
                        <details className="small text-muted" aria-label="Salesforce sync error details">
                          <summary className="cursor-pointer" aria-label="Expand Salesforce sync error details">Error Details</summary>
                          <p className="mt-1 mb-0">{sfdcError}</p>
                        </details>
                      </InlineAlert>
                    ) : null}
                  </div>
                )}

                {/* SAP Integration Status */}
                {isSapAvailable() && (
                  <div className="mb-3" aria-label="SapIntegrationStatus">
                    {sapSuccess ? (
                      <InlineAlert
                        variant="info"
                        icon="database-check"
                        title="SAP Integration Successful"
                        message="Customer Business Partner record has been created in SAP system."
                        className="border-0 bg-light"
                        ariaLabel="SAP integration successful alert"
                      >
                        {sapBusinessPartnerId && (
                          <p className="mb-0 small">
                            <strong>SAP Business Partner ID:</strong> <span className="font-monospace text-primary" aria-label="SapBusinessPartnerId">{sapBusinessPartnerId}</span>
                          </p>
                        )}
                      </InlineAlert>
                    ) : sapError ? (
                      <InlineAlert
                        variant="warning"
                        icon="exclamation-triangle"
                        title="SAP Sync Warning"
                        message="Customer created locally but SAP Business Partner sync failed. The IT team has been notified."
                        className="border-0 bg-light"
                        ariaLabel="SAP sync warning alert"
                      >
                        <details className="small text-muted" aria-label="SAP sync error details">
                          <summary className="cursor-pointer" aria-label="Expand SAP sync error details">Error Details</summary>
                          <p className="mt-1 mb-0">{sapError}</p>
                        </details>
                      </InlineAlert>
                    ) : null}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <Link to="/dashboard" className="btn btn-tse-primary" aria-label="ReturnToDashboardButton">
                    <i className="bi bi-house me-2"></i>Return to Dashboard
                  </Link>
                  <button 
                    type="button" 
                    className="btn btn-outline-tse-green"
                    aria-label="CreateAnotherCustomerButton"
                    onClick={() => {
                      setCurrentStep(0);
                      setIsApiLoading(false);
                      setApiError(null);
                      setApiErrorDetails(null);
                      setIsPaymentProcessing(false);
                      setPaymentSuccess(false);
                      setIsCreatingCustomer(false);
                      setHasAttemptedCreditCheck(false);
                      setSfdcError(null);
                      setSfdcSuccess(false);
                      setSfdcAccountId(null);
                      setSapError(null);
                      setSapSuccess(false);
                      setSapBusinessPartnerId(null);
                      setCustomerData({
                        firstName: '',
                        lastName: '',
                        phone: '',
                        email: '',
                        ssnLast4: '',
                        streetAddress: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        cardNumber: '',
                        expiry: '',
                        cvv: '',
                        billingZip: '',
                        serviceStartDate: getCurrentDate(),
                        serviceType: 'residential',
                        _autoPopulated: false
                      });
                      setLookupData({ phone: '', email: '' });
                      setCreditResults(null);
                      setShowResults(false);
                    }}
                  >
                    <i className="bi bi-person-plus me-2"></i>Create Another Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Start Over Confirmation Dialog */}
      {showStartOverDialog && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Start Over?
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  You are currently in the process of creating a new customer account. 
                  Starting over will clear all entered information.
                </p>
                <p className="mb-0 text-muted">
                  <strong>Are you sure you want to start over?</strong>
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={handleCancelStartOver}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleStartOver}
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  OK, Start Over
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Creation Loading Overlay */}
      {isCreatingCustomer && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center customer-create-loading"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.7)', 
            zIndex: 9999,
            backdropFilter: 'blur(2px)'
          }}
        >
          <div className="text-center">
            <div className="card border-0 shadow-lg customer-create-loading__card" style={{ minWidth: '300px' }}>
              <div className="card-body p-4">
                <div className="mb-3">
                  <div className="spinner-border text-success customer-create-loading__spinner" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <h5 className="card-title text-success mb-2 customer-create-loading__title">Creating Customer Account</h5>
                <p className="card-text text-muted mb-0 customer-create-loading__copy">
                  Please wait while we set up your customer account, sync with Salesforce CRM, and schedule the service connection...
                </p>
                <div className="progress mt-3 customer-create-loading__progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-success progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewCustomer;
